import React from "react";
import { CheckCircle2, AlertCircle, FileText, Loader2 } from "lucide-react";

const SobBlock = ({ sob, testid }) => {
  if (!sob) return null;
  const docs = (sob.documents || []).filter((d) => d.status !== "unavailable");
  const ready = docs.filter((d) => d.status === "extracted");
  if (sob.status === "extracting" && !ready.length) return <p className="flex items-center gap-2 text-sm text-[#5A6B7B]" data-testid={`${testid}-reading`}><Loader2 className="h-3.5 w-3.5 animate-spin" />Reading the official plan documents…</p>;
  if (!docs.length) return <p className="text-sm text-[#5A6B7B]" data-testid={`${testid}-not-linked`}>Official plan documents: not linked yet. Figures shown come from CMS 2027 plan data.</p>;
  return (
    <div className="space-y-1.5 text-sm" data-testid={testid}>
      {docs.map((d, i) => (
        <a key={d.url} href={d.url} target="_blank" rel="noopener noreferrer" className="mr-3 inline-flex items-center gap-1.5 font-semibold text-[#1E3E62] underline" data-testid={`${testid}-link${i ? `-${i}` : ""}`}>
          <FileText className="h-4 w-4" />Official 2027 {d.label} ({sob.publisher})
        </a>
      ))}
      {sob.status === "extracting" && <p className="flex items-center gap-2 text-xs text-[#5A6B7B]" data-testid={`${testid}-still-reading`}><Loader2 className="h-3 w-3 animate-spin" />Still reading one document…</p>}
      {(sob.facts || []).length > 0 && (
        <details className="group" data-testid={`${testid}-details`}>
          <summary className="cursor-pointer min-h-11 flex items-center font-semibold text-[#1E3E62]">What the plan document says ({sob.facts.length})</summary>
          {sob.facts.map((f, i) => (
            <p key={i} className="mt-1 text-[#33475B]" data-testid={`${testid}-fact-${i}`}>
              {f.text} <span className="text-[#5A6B7B]">({f.cite_text || `p.${f.page}`})</span>
              {f.discrepancy_with_cms && <span className="ml-1 text-[#8A4B08]"> · differs from CMS data — confirm with the plan</span>}
            </p>
          ))}
        </details>
      )}
    </div>
  );
};

export const CaseChoices = ({ choices, selected, onToggleSelect, busy, currentId, onSetCurrent, onViewCompare, agent }) => (
  <div className="grid gap-4 lg:grid-cols-3" data-testid="case-choices">
    {choices.map((c) => {
      const isSel = (selected || []).some((s) => s.display_id === c.display_id);
      return (
        <article key={c.display_id} className="flex flex-col rounded-xl border border-[#D9D6CC] bg-white p-5" data-testid={`case-choice-${c.rank}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#8A6D0B]">Choice {c.rank}{c.provisional ? " · provisional" : ""}</p>
          <h3 className="mt-1 text-lg font-semibold leading-snug text-[#0B192C]" data-testid={`case-choice-${c.rank}-name`}>{c.plan_name}</h3>
          <p className="text-sm text-[#5A6B7B]">{c.carrier} · {c.display_id} · {c.plan_year}</p>
          {currentId === c.display_id && <p className="mt-1 inline-flex w-fit rounded-full bg-[#EEF2F6] px-2.5 py-0.5 text-xs font-semibold text-[#1E3E62]" data-testid={`case-choice-${c.rank}-current-badge`}>{agent ? "Client says this is their current plan" : "You said this is your current plan"} · not verified</p>}
          <ul className="mt-3 space-y-1.5 text-sm">
            {c.why.map((w, i) => <li key={i} className="flex gap-2 text-[#1F5132]"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{w}</li>)}
            {c.tradeoffs.map((t, i) => <li key={`t${i}`} className="flex gap-2 text-[#5A4A1A]"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{t}</li>)}
          </ul>
          {c.missing_checks.length > 0 && (
            <div className="mt-3 rounded-lg bg-[#F4F1E8] p-3 text-sm" data-testid={`case-choice-${c.rank}-checks`}>
              <p className="font-semibold text-[#0B192C]">Still to confirm</p>
              <ul className="mt-1 list-disc pl-5 text-[#33475B]">{c.missing_checks.map((k, i) => <li key={i}>{k.text}</li>)}</ul>
            </div>
          )}
          <div className="mt-3"><SobBlock sob={c.sob} testid={`case-choice-${c.rank}-sob`} /></div>
          {onToggleSelect && (
            <div className="mt-auto pt-4 space-y-2">
              {isSel ? (
                <div className="flex gap-2">
                  <button type="button" onClick={onViewCompare} className="h-11 flex-1 rounded-full border border-[#1E3E62] bg-[#1E3E62] text-sm font-semibold text-white" data-testid={`case-choice-${c.rank}-view-compare`}>Added to comparison · View</button>
                  <button type="button" disabled={busy} onClick={() => onToggleSelect(c.display_id)} className="h-11 rounded-full border border-[#BCCCDC] px-3 text-sm text-[#1E3E62]" data-testid={`case-choice-${c.rank}-select`}>Remove from comparison</button>
                </div>
              ) : (
                <button type="button" disabled={busy} onClick={() => onToggleSelect(c.display_id)} className="h-11 w-full rounded-full border border-[#BCCCDC] text-sm font-semibold text-[#1E3E62]" data-testid={`case-choice-${c.rank}-select`}>Add to comparison</button>
              )}
              {onSetCurrent && currentId !== c.display_id && (
                <button type="button" disabled={busy} onClick={() => onSetCurrent(c.display_id)} className="min-h-11 w-full text-sm text-[#1E3E62] underline" data-testid={`case-choice-${c.rank}-set-current`}>{agent ? "Client is on this plan now" : "This is my current plan"}</button>
              )}
            </div>
          )}
        </article>
      );
    })}
  </div>
);
